import React, { useState, useEffect, useRef } from 'react';
import { keyCode } from '../../helpers';
import { Icon, Label, formatOptionTooltip } from '../';
import { useTooltip } from '../../hooks';

import { ValidationMessage } from '../ValidationMessage';
import './SelectButtons.css';

/**
 * The visual variants this control offers, mapped to the class that implements each. Callers name a
 * design rather than a class so which rules produce it stays an implementation detail in here.
 */
const DESIGNS = {
  pill: 'pill-design',
  slim: 'slim-design',
  bright: 'bright-design',
  'tab-button': 'tab-button-design',
};

/**
 * A row of mutually exclusive options rendered as buttons — a segmented control.
 *
 * Used both as a form control and, outside forms, as a selector: the view-mode switch in a table
 * toolbar, the language picker in the technical definition toolbar, the kind filter above a
 * dataset group's table.
 *
 * @param {object} props
 * @param {string} props.name                    - Field name, returned in the onChange payload.
 * @param {*} props.value                        - The selected option's value.
 * @param {string|boolean} [props.label]         - Label text; see the Label component.
 * @param {object[]} props.options               - `{ value, label, secondaryLabel, icon, tooltip,
 *                                                 className, disabled }` per option.
 * @param {function} props.onChange              - Called with `{ name, value }`.
 * @param {boolean} [props.isNullable]           - Clicking the selected option clears it.
 * @param {boolean} [props.disabled=false]       - Disables the whole control.
 * @param {string} [props.title]                 - Tooltip description for the label.
 * @param {string} [props.className]             - Additional CSS class names.
 * @param {boolean} [props.isOptional=false]     - Shows an "optional" badge on the label.
 * @param {object} [props.validation]            - Validation state; renders a ValidationMessage.
 * @param {'pill'|'slim'|'bright'|'tab-button'} [props.design] - Visual variant. Omit for the
 *                                                 default form-control appearance.
 */
export const SelectButtons = ({
  name,
  value,
  label,
  options,
  onChange,
  isNullable,
  disabled = false,
  title,
  displayNameCode,
  className,
  isOptional = false,
  validation,
  design,
}) => {
  const { showTooltip, hideTooltip } = useTooltip();

  const refOptions = useRef([]);

  const SPACEBAR_KEY_CODE = 32; //[0, 32];
  const ENTER_KEY_CODE = 13;
  const LEFT_ARROW_KEY_CODE = 37;
  const RIGHT_ARROW_KEY_CODE = 39;
  const ESCAPE_KEY_CODE = 27;

  useEffect(() => {
    refOptions.current = new Array(options.length);
  }, []);

  const focusNextListItem = direction => {
    const activeElement = document.activeElement;

    const activeElementIndex = refOptions.current.findIndex(el => el === activeElement);
    const activeOptionIndex =
      activeElementIndex < 0 ? options.findIndex(el => el.value === value) : activeElementIndex;

    if (activeOptionIndex >= 0) {
      if (direction === RIGHT_ARROW_KEY_CODE && activeOptionIndex + 1 < refOptions.current.length) {
        refOptions.current[activeOptionIndex + 1].focus();
      } else if (direction === LEFT_ARROW_KEY_CODE && activeOptionIndex > 0) {
        refOptions.current[activeOptionIndex - 1].focus();
      }
    } else {
      refOptions.current[0].focus();
    }
  };

  const handleSelectOption = val => {
    if (val === value && isNullable) val = null;
    onChange({ name: name, value: val });
  };

  const handleKeyDown = (e, val) => {
    switch (e.keyCode) {
      case ENTER_KEY_CODE:
      case SPACEBAR_KEY_CODE:
        if (val !== undefined) {
          handleSelectOption(val);
        }
        break;

      case LEFT_ARROW_KEY_CODE:
        focusNextListItem(LEFT_ARROW_KEY_CODE);
        break;

      case RIGHT_ARROW_KEY_CODE:
        focusNextListItem(RIGHT_ARROW_KEY_CODE);
        break;

      case ESCAPE_KEY_CODE:
        break;

      default:
        break;
    }
  };

  const optionIsSelected = match => {
    return value === match ? true : false;
  };

  const hasSecondaryLabels = options.find(x => x.secondaryLabel);

  return (
    <div
      className={
        'CustomSelectButtons' +
        (disabled ? ' disabled' : '') +
        (DESIGNS[design] ? ` ${DESIGNS[design]}` : '') +
        (className ? ` ${className}` : '') +
        (validation && !validation.valid ? ' has-error' : '') +
        (hasSecondaryLabels ? ' has-secondary-labels' : '')
      }
    >
      <div className="select-wrapper">
        <Label
          label={label}
          htmlFor={name}
          displayNameCode={displayNameCode}
          title={title}
          disabled={disabled}
          isOptional={isOptional}
        />
        <div className={'select-container'}>
          <div className="select-options" role="listbox">
            {options.length > 0 &&
              options.map((option, i) => (
                <div
                  className={
                    'select-option' +
                    (optionIsSelected(option.value) ? ' active' : '') +
                    (option.disabled ? ' disabled' : '') +
                    (option.label ? '' : ' no-label')
                  }
                  key={option.value}
                  ref={el => (refOptions.current[i] = el)}
                  aria-label={option.label}
                  aria-selected={optionIsSelected(option.value)}
                  role="option"
                  tabIndex={0}
                  onClick={() => {
                    if (!disabled && !option.disabled) handleSelectOption(option.value);
                  }}
                  onKeyDown={e => {
                    if (!disabled && !option.disabled) handleKeyDown(e, option.value);
                  }}
                  onMouseEnter={() => showTooltip(formatOptionTooltip(option))}
                  onMouseLeave={hideTooltip}
                >
                  {option.icon && (
                    <div className="select-option-icon">
                      {' '}
                      <Icon name={option.icon} />
                    </div>
                  )}
                  {option.label && (
                    <div
                      className={
                        'select-option-label' + (option.className ? ' ' + option.className : '')
                      }
                    >
                      <div>{option.label}</div>
                      {option.secondaryLabel && (
                        <div className="select-option-secondary-label">{option.secondaryLabel}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
      <ValidationMessage validation={validation} />
    </div>
  );
};
